/**
 * LDA Unavailable Modal Component
 * Blocking modal shown when LDA service is unavailable (circuit breaker open).
 * User can retry connection.
 * @packageDocumentation
 */

import { useTranslation } from 'react-i18next';
import { RefreshCw, ServerCrash } from 'lucide-react';
import * as DialogPrimitive from '@radix-ui/react-dialog';

import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores';
import { Button } from '@/components/ui/button';

/**
 * LDA unavailable modal that blocks page access when authentication service is down.
 * Shows error details and a retry button.
 *
 * @returns { JSX.Element | null } The LDA unavailable modal or null if available.
 */
export const LdaUnavailableModal = () => {
  const { t } = useTranslation('pages');
  const { error, isLoading, checkSession, ldaUnavailable } = useAuthStore();

  if (!ldaUnavailable) {
    return null;
  }

  const handleRetry = async () => {
    useAuthStore.setState({
      error: null,
      ldaUnavailable: false,
    });
    await checkSession();
  };

  return (
    <DialogPrimitive.Root open modal>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            'fixed inset-0 z-50 bg-black/80',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          )}
        />
        <DialogPrimitive.Content
          className={cn(
            'fixed left-[50%] top-[50%] z-50 grid w-full max-w-md',
            'translate-x-[-50%] translate-y-[-50%]',
            'gap-4 border bg-background p-6 shadow-lg duration-200',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
            'data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]',
            'data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]',
            'sm:rounded-lg',
          )}
          onEscapeKeyDown={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
        >
          <div className="flex flex-col items-center text-center space-y-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
              <ServerCrash className="h-8 w-8 text-destructive" />
            </div>

            <div className="space-y-2">
              <DialogPrimitive.Title className="text-lg font-semibold leading-none tracking-tight">
                {t('auth.ldaModal.title')}
              </DialogPrimitive.Title>
              <DialogPrimitive.Description className="text-sm text-muted-foreground">
                {t('auth.ldaModal.description')}
              </DialogPrimitive.Description>
            </div>

            {error?.message && (
              <div className="w-full rounded-md bg-muted p-3 text-left">
                <p className="text-xs text-muted-foreground font-mono break-all">{error.message}</p>
              </div>
            )}

            <Button
              onClick={handleRetry}
              loading={isLoading}
              className="w-full"
              variant="default"
              leftIcon={<RefreshCw />}
            >
              {isLoading ? t('auth.ldaModal.retrying') : t('auth.ldaModal.retryConnection')}
            </Button>

            <p className="text-xs text-muted-foreground">{t('auth.ldaModal.persistsContact')}</p>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};
